import { useEffect, forwardRef, ReactNode } from 'react';

interface ScrollableWrapperProps {
  children: ReactNode;
  className?: string;
  scrollSpeed?: number;
  dragSpeed?: number;
}

const ScrollableWrapper = forwardRef<HTMLDivElement, ScrollableWrapperProps>(
  ({ children, className = '', scrollSpeed = 50, dragSpeed = 1.5 }, ref) => {
    useEffect(() => {
      const scrollContainer = (ref as React.RefObject<HTMLDivElement>).current;
      if (!scrollContainer) return () => {};

      let isDragging = false;
      let startX: number;
      let scrollLeft: number;

      const handleMouseDown = (e: MouseEvent) => {
        isDragging = true;
        startX = e.pageX - scrollContainer.offsetLeft;
        scrollLeft = scrollContainer.scrollLeft;
        scrollContainer.style.cursor = 'grabbing';
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - scrollContainer.offsetLeft;
        const walk = (x - startX) * dragSpeed;
        scrollContainer.scrollLeft = scrollLeft - walk;
      };

      const handleMouseUp = () => {
        isDragging = false;
        scrollContainer.style.cursor = 'grab';
      };

      const handleMouseLeave = () => {
        isDragging = false;
        scrollContainer.style.cursor = 'grab';
      };

      const handleWheel = (e: WheelEvent) => {
        e.preventDefault();
        scrollContainer.scrollLeft += e.deltaY > 0 ? scrollSpeed : -scrollSpeed;
      };

      scrollContainer.addEventListener('mousedown', handleMouseDown);
      scrollContainer.addEventListener('mousemove', handleMouseMove);
      scrollContainer.addEventListener('mouseup', handleMouseUp);
      scrollContainer.addEventListener('mouseleave', handleMouseLeave);
      scrollContainer.addEventListener('wheel', handleWheel);

      scrollContainer.style.cursor = 'grab';

      return () => {
        scrollContainer.removeEventListener('mousedown', handleMouseDown);
        scrollContainer.removeEventListener('mousemove', handleMouseMove);
        scrollContainer.removeEventListener('mouseup', handleMouseUp);
        scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
        scrollContainer.removeEventListener('wheel', handleWheel);
      };
    }, [scrollSpeed, dragSpeed, ref]);

    return (
      <div ref={ref} className={`overflow-x-auto whitespace-nowrap ${className}`}>
        {children}
      </div>
    );
  },
);

ScrollableWrapper.displayName = 'ScrollableWrapper';

export default ScrollableWrapper;
